/**
 * Scan: an unattended tour of the incident list.
 *
 * This is not the old scan. That one swept the map back and forth on a fixed
 * path, which looked purposeful and showed you nothing in particular — the
 * camera was moving whether or not anything was under it. This one visits the
 * incidents themselves, in the order the browser lists them, so every stop is
 * somewhere something actually happened.
 *
 * North is restored on start and held for the whole tour. A board that rotates
 * between stops is disorienting to watch from across a room, and the point of
 * an unattended mode is that nobody is driving.
 */

/** Exported so the scan card's progress bar runs for exactly this long. */
export const DWELL_MS = 15000;

/** Close enough to read the block, high enough to keep the surroundings. */
const SCAN_ZOOM = 17.4;

export function mountScan({ map, layers, getNow, onChange, onTarget, onRunChange } = {}) {
  const button = document.getElementById("scan");
  if (!button || !map) return { destroy() {} };

  let timer = null;
  let index = 0;

  const running = () => timer !== null;

  function incidents() {
    const layer = layers?.find((l) => l.key === "incidents");
    if (!layer || layer.status !== "ready") return [];
    // Newest first, matching the browser list, so the tour order is the order
    // on screen rather than a second opinion about it.
    return layer.recent(getNow()).slice().reverse();
  }

  function goTo(row) {
    map.flyTo({
      center: [row.x, row.y],
      zoom: SCAN_ZOOM,
      pitch: 0,
      bearing: 0,
      duration: 2200,
      essential: true,
    });
  }

  function step() {
    const rows = incidents();
    if (!rows.length) return stop();
    // Wrap rather than stop: this loops until someone turns it off.
    index %= rows.length;
    const row = rows[index];
    index += 1;
    goTo(row);
    // The map tells you where it is going; the pulse tells you what it went
    // there for, which is otherwise one teal dot among dozens.
    onTarget?.(row, { index, total: rows.length });
    button.title = `Scanning ${index}/${rows.length} — click to stop (P)`;
    if (onChange) onChange();
  }

  function start() {
    const rows = incidents();
    if (!rows.length) {
      button.title = "Nothing to scan — no incidents in the window";
      return;
    }
    index = 0;
    button.setAttribute("aria-pressed", "true");
    button.textContent = "STOP";

    // Face north first, then begin. The first hop then only moves the camera,
    // which reads as arriving somewhere rather than being spun around.
    map.easeTo({ bearing: 0, pitch: 0, duration: 600, essential: true });
    // Announced before the first hop, so whatever is listening can get out of
    // the way before the camera starts moving rather than a beat after it.
    onRunChange?.(true);
    step();
    timer = setInterval(step, DWELL_MS);
  }

  function stop() {
    if (timer !== null) clearInterval(timer);
    timer = null;
    onTarget?.(null);
    button.setAttribute("aria-pressed", "false");
    button.textContent = "SCAN";
    button.title = "Tour incidents, 15s each (P)";
    onRunChange?.(false);
    if (onChange) onChange();
  }

  function toggle() {
    if (running()) stop(); else start();
  }

  button.addEventListener("click", toggle);

  // Any deliberate camera input is a takeover: stop rather than fight it.
  for (const event of ["dragstart", "wheel"]) {
    map.on(event, () => { if (running()) stop(); });
  }

  return { start, stop, toggle, running, destroy: stop };
}
